import React, { useState } from 'react';
import TranslationHelper from '../../../../../helpers/TranslationHelper';
import { Field, FormikErrors, FormikValues } from 'formik';
import CheckboxWrapper from '../../../../common/formikWrappers/CheckboxWrapper';
import {
    Box,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    MenuItem,
} from '@mui/material';
import FieldWrapper from '../../../../common/formikWrappers/FieldWrapper';
import { restGetRegistersMonitoredObjects } from '@/app/services/registers/monitoredObjects';
import { TDeviceDataTabValues } from './types';
import NumberFieldWrapper from '../../../../common/formikWrappers/NumberFieldWrapper';
import CustomAutocomplete from '@/app/components/common/CustomAutocomplete';
import { TDeviceDialogType, DEVICE_TYPES } from '../types';
import { styled } from '@mui/system';

const StyledField = styled(Field)({
    flex: 1,
    '& .MuiMenu-list': {
        display: 'block',
        '& .MuiButtonBase-root': {
            display: 'flex !important',
        },
    },
});

type Props = {
    errors?: FormikErrors<TDeviceDataTabValues>;
    mode: TDeviceDialogType;
    values: FormikValues;
    setFieldValue: (name: string, value: any) => void;
};

type PasswordFieldsProps = {
    errors?: FormikErrors<TDeviceDataTabValues>;
    required?: boolean;
    labelPrefix?: string;
};

export const PasswordFields = ({ errors, required }: PasswordFieldsProps) => {
    return (
        <>
            <Field
                name="data.password"
                label={TranslationHelper.translate('New password')}
                component={FieldWrapper}
                fullWidth
                autoComplete="new-password"
                type="password"
                required={required}
                error={Boolean(errors?.password)}
                helperText={errors?.password}
            />
            <Field
                name="data.passwordConfirmation"
                label={TranslationHelper.translate('Confirm new password')}
                autoComplete="new-password-confirm"
                type="password"
                component={FieldWrapper}
                fullWidth
                required={required}
            />
        </>
    );
};

export const DataTab = ({ errors, mode, values, setFieldValue }: Props) => {
    const [idFieldVisible, setIdFieldVisible] = useState(false);
    const [newPasswordChecked, setNewPasswordChecked] = useState(false);

    const handleAutoGenerate = (enabled: boolean) => {
        setIdFieldVisible(!enabled);
    };

    const editMode = mode === 'edit';

    const selectedDevice = DEVICE_TYPES.find(
        (d) => d.id === values?.data?.deviceType
    );

    const showPasswordFields = selectedDevice?.canSetPassword;

    return (
        <>
            {!editMode && (
                <div>
                    <FormControlLabel
                        label={TranslationHelper.translate('Auto-generate ID')}
                        control={
                            <Field
                                name="data.autoGenerateId"
                                component={CheckboxWrapper}
                                onClick={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => handleAutoGenerate(e.target.checked)}
                            />
                        }
                    />
                </div>
            )}
            {idFieldVisible && !editMode && (
                <Field
                    name="data.id"
                    label={TranslationHelper.translate('ID')}
                    component={NumberFieldWrapper}
                    fullWidth
                    error={Boolean(errors?.id)}
                    helperText={errors?.id}
                />
            )}
            <StyledField
                name="data.deviceType"
                label={TranslationHelper.translate('Device type')}
                component={FieldWrapper}
                required
                select
                fullWidth
                error={Boolean(errors?.deviceType)}
                helperText={errors?.deviceType}
            >
                {DEVICE_TYPES.map((val) => (
                    <MenuItem key={val.id} value={val.id}>
                        {val.translate
                            ? TranslationHelper.translate(val.name)
                            : val.name}
                    </MenuItem>
                ))}
            </StyledField>

            <Field
                name="data.serialNumber"
                label={TranslationHelper.translate('Serial number')}
                component={FieldWrapper}
                fullWidth
                required
                autoComplete="one-time-code"
                error={Boolean(errors?.serialNumber)}
                helperText={errors?.serialNumber}
                InputProps={{
                    startAdornment:
                        selectedDevice && selectedDevice.id !== 'other' ? (
                            <InputAdornment position="start">
                                {selectedDevice.id}
                            </InputAdornment>
                        ) : null,
                }}
            />
            {showPasswordFields && (
                <>
                    {mode === 'create' ? (
                        <PasswordFields errors={errors} required />
                    ) : (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newPasswordChecked}
                                        tabIndex={-1}
                                        disableRipple
                                        onChange={() =>
                                            setNewPasswordChecked(
                                                (checked) => !checked
                                            )
                                        }
                                        inputProps={{
                                            'aria-labelledby': 'newPassword',
                                        }}
                                    />
                                }
                                label={TranslationHelper.translate(
                                    'Change password'
                                )}
                                sx={{
                                    my: 1,
                                }}
                            />
                            {newPasswordChecked && (
                                <PasswordFields
                                    errors={errors}
                                    required={!editMode}
                                />
                            )}
                        </>
                    )}
                </>
            )}
            <Box
                sx={{
                    my: 1,
                }}
            >
                <CustomAutocomplete
                    value={values.data?.monitoredObjectId}
                    inputTexts={{
                        placeholder:
                            TranslationHelper.translate('Select object'),
                    }}
                    label={TranslationHelper.translate('Object')}
                    onChange={(e, value) => {
                        setFieldValue('data.monitoredObjectId', value);
                    }}
                    onOpen={restGetRegistersMonitoredObjects}
                    optionKeys={{
                        name: 'name',
                        value: 'id',
                    }}
                    multi={false}
                    error={errors?.monitoredObjectId}
                />
            </Box>
        </>
    );
};
